import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Youtube, Shield, ExternalLink, Radio, Award, Users, FileCheck, MousePointerClick, Film } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SafeVideoEmbed, VideoCredit } from '../components/SafeVideoEmbed';
import { SafeMicrositeEmbed } from '../components/SafeMicrositeEmbed';
import { ContentDisclaimer } from '../components/ContentDisclaimer';
import { PageBreadcrumb } from '../components/PageBreadcrumb';
import { OFFICIAL_CHANNELS, OFFICIAL_PLAYLISTS, OFFICIAL_VIDEOS } from '../data/officialVideos';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb } from '../seo/breadcrumbs';

/**
 * Page Ressources officielles — vidéos & playlists de la Sécurité routière.
 *
 * Vise à enrichir la pédagogie de Code 2 Roues avec des contenus officiels
 * (Sécurité routière, Fondation VINCI Autoroutes, Prévention Routière) intégrés
 * via YouTube en mode privacy-enhanced (RGPD-safe), sans héberger ni dupliquer.
 */
export default function RessourcesOfficielles() {
  useSeo({
    title: 'Ressources officielles moto — Vidéos Sécurité routière',
    description: "Regardez les campagnes et vidéos pédagogiques officielles de la Sécurité routière pour compléter votre préparation au code moto (ETM). Source officielle, intégration RGPD-safe.",
    path: '/ressources-officielles',
    jsonLd: buildBreadcrumb([
      ['Ressources officielles', '/ressources-officielles'],
    ]),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="ressources-officielles-page">
      <PageBreadcrumb items={[{ label: 'Ressources officielles' }]} />
      {/* Back */}
      <div className="flex items-center gap-2">
        <Link to="/" data-testid="ressources-back">
          <Button variant="ghost" size="sm" className="-ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'accueil
          </Button>
        </Link>
      </div>

      {/* Hero */}
      <section className="mc-card relative overflow-hidden p-6 sm:p-10" data-testid="ressources-hero">
        <div
          className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20"
          style={{ background: 'hsl(var(--primary))' }}
        />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-2 inline-flex items-center gap-1.5">
            <Radio className="w-3 h-3" /> Ressources complémentaires
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
            Campagnes officielles
            <br />
            <span className="text-[hsl(var(--primary))]">de la Sécurité routière</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl leading-relaxed">
            Pour approfondir ta préparation à l'ETM, retrouve ici les{' '}
            <strong className="text-foreground">vidéos pédagogiques officielles</strong> de la
            Sécurité routière, de la Fondation VINCI Autoroutes et de la Prévention Routière.
            Contenus <strong className="text-foreground">gratuits, publics et officiels</strong>.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 text-green-500 border border-green-500/30 px-2.5 py-1 font-bold uppercase tracking-widest">
              <Shield className="w-3 h-3" /> RGPD-safe
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/30 px-2.5 py-1 font-bold uppercase tracking-widest">
              <Award className="w-3 h-3" /> 100 % officiel
            </span>
            <span className="text-muted-foreground italic">
              Aucun cookie avant que tu cliques sur lecture.
            </span>
          </div>
        </div>
      </section>

      {/* Simulateur officiel Équipez-vous (Sécurité routière) — Featured */}
      <section className="mc-card p-0 overflow-hidden" data-testid="equipez-vous-section">
        <div className="p-5 sm:p-6 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary)/_0.1)] text-[hsl(var(--primary))]">
              <MousePointerClick className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Simulateur interactif · #EquipezVous
              </span>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
                Teste ton niveau d'équipement moto
              </h2>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Simulateur officiel de la <strong className="text-foreground">Sécurité routière</strong> :
            choisis un personnage, ajuste le curseur de protection et découvre les équipements
            adaptés (casque, blouson, airbag, gants, pantalon, bottes) avec les normes CE et
            les risques associés à chaque choix.
          </p>
        </div>

        <div className="p-5 sm:p-6 pt-0 sm:pt-0">
          <SafeMicrositeEmbed
            src="https://www.securite-routiere.gouv.fr/operations/equipezvous/home"
            title="+ vous êtes équipé, + vous êtes protégé"
            subtitle="Simulateur interactif officiel : ajuste le curseur de protection et découvre les équipements moto adaptés (casque, gants, blouson, airbag…) avec les normes CE."
            sourceName="Sécurité routière"
            sourceUrl="https://www.securite-routiere.gouv.fr/"
            mode="fullscreen"
            testid="equipez-vous-embed"
          />
        </div>

        <div className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-2 text-[11px]">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Shield className="w-3 h-3" /> Source officielle gouvernementale
            </span>
            <span>·</span>
            <span>#EquipezVous</span>
          </div>
          <a
            href="https://www.securite-routiere.gouv.fr/operations/equipezvous/home"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
            data-testid="equipez-vous-source-link"
          >
            <span>Voir sur securite-routiere.gouv.fr</span>
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      </section>

      {/* Campagnes chocs — vidéos individuelles */}
      {OFFICIAL_VIDEOS.length > 0 && (
        <section className="mc-card p-5 sm:p-6" data-testid="campaigns-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary)/_0.1)] text-[hsl(var(--primary))]">
              <Film className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Campagnes nationales à fort impact
              </span>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
                Vidéos officielles — à visionner absolument
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
            {OFFICIAL_VIDEOS.map((v) => (
              <article
                key={v.videoId}
                className="rounded-xl border border-border overflow-hidden bg-[hsl(var(--panel))]"
                data-testid={`video-card-${v.videoId}`}
              >
                <div className="bg-black">
                  <SafeVideoEmbed
                    videoId={v.videoId}
                    title={v.title}
                    channel={v.channel}
                    testid={`official-video-${v.videoId}`}
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-black text-sm sm:text-base leading-tight mb-1.5">
                    {v.title}
                  </h3>
                  {v.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                      {v.description}
                    </p>
                  )}
                  <VideoCredit
                    channel={v.channel}
                    channelUrl={v.channelUrl || 'https://www.youtube.com/@securiteroutiere'}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Chaînes officielles */}
      <section className="mc-card p-5 sm:p-6" data-testid="official-channels-section">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary)/_0.1)] text-[hsl(var(--primary))]">
            <Users className="w-5 h-5" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">
            Chaînes officielles à suivre
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {OFFICIAL_CHANNELS.map((ch) => (
            <a
              key={ch.handle}
              href={ch.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-xl border border-border bg-[hsl(var(--panel))] p-4 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/_0.04)] transition-colors"
              data-testid={`channel-${ch.handle.replace('@', '')}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-9 h-9 rounded-lg grid place-items-center bg-red-500/10 text-red-500 flex-shrink-0">
                  <Youtube className="w-5 h-5" />
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-60 group-hover:text-[hsl(var(--primary))] transition-colors flex-shrink-0" />
              </div>
              <h3 className="font-black text-sm leading-tight mb-1">{ch.name}</h3>
              <p className="text-[11px] text-muted-foreground font-mono mb-2">{ch.handle}</p>
              <p className="text-xs text-foreground/80 leading-relaxed">{ch.description}</p>
              <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
                <span>{ch.subscribers} abonnés</span>
                <span>·</span>
                <span>{ch.videoCount} vidéos</span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Playlists thématiques intégrées */}
      <section className="mc-card p-5 sm:p-6" data-testid="official-playlists-section">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-[hsl(var(--primary)/_0.1)] text-[hsl(var(--primary))]">
            <FileCheck className="w-5 h-5" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight">
            Playlists pédagogiques à visionner
          </h2>
        </div>

        <div className="space-y-6">
          {OFFICIAL_PLAYLISTS.map((pl) => (
            <article
              key={pl.id}
              className="rounded-xl border border-border overflow-hidden"
              data-testid={`playlist-${pl.id}`}
            >
              <div className="p-4 sm:p-5 bg-[hsl(var(--panel))]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                    {pl.kicker}
                  </span>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <span className="text-[10px] text-muted-foreground">{pl.channel}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-black tracking-tight">{pl.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{pl.description}</p>
              </div>
              <div className="p-4 sm:p-5 bg-black">
                <SafeVideoEmbed
                  listId={pl.listId}
                  title={pl.title}
                  channel={pl.channel}
                  testid={`video-${pl.id}`}
                />
                <VideoCredit
                  channel={pl.channel}
                  channelUrl="https://www.youtube.com/@securiteroutiere"
                  className="mt-3"
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Avertissement + mention d'usage */}
      <section className="mc-card p-5 sm:p-6" data-testid="usage-notice-section">
        <h2 className="text-base sm:text-lg font-black tracking-tight mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[hsl(var(--primary))]" />
          Comment ces contenus sont intégrés
        </h2>
        <div className="space-y-2.5 text-sm text-foreground/85 leading-relaxed">
          <p>
            Les vidéos ci-dessus sont <strong>intégrées directement depuis YouTube</strong> via le
            lecteur officiel en mode <em>privacy-enhanced</em> ({' '}
            <code className="text-[11px] bg-[hsl(var(--panel))] px-1.5 py-0.5 rounded">youtube-nocookie.com</code>
            ). Tant que tu ne lances pas la lecture, <strong>aucun cookie n'est déposé</strong>{' '}
            et <strong>aucune donnée n'est transmise à YouTube</strong>.
          </p>
          <p>
            Code 2 Roues <strong>ne copie, ne reproduit, ni ne re-héberge</strong> ces vidéos.
            Les contenus restent la propriété intellectuelle de leurs producteurs respectifs
            (Sécurité routière, Fondation VINCI Autoroutes, Prévention Routière), qui autorisent
            explicitement leur intégration via YouTube selon leurs conditions d'utilisation.
          </p>
          <p className="text-xs text-muted-foreground italic">
            Pour consulter l'ensemble des campagnes, visite directement les chaînes officielles
            ci-dessus. Toute suggestion de vidéo pédagogique pertinente peut être envoyée à{' '}
            <a
              href="mailto:contact@code2roues.fr?subject=Suggestion%20vid%C3%A9o%20officielle"
              className="underline hover:text-[hsl(var(--primary))]"
            >
              contact@code2roues.fr
            </a>
            .
          </p>
        </div>
      </section>

      <ContentDisclaimer compact testid="ressources-disclaimer" />
    </div>
  );
}
